import axios from '../axios-config';

export interface HallPassResponse {
  hallPassId: string;
  hallPassStatusId: number;
  hallPassTypeId: string;
  createdAt: string;
  requestAt: string;
  requestDecisionedAt: string | null;
  returnAt: string | null;
  studentId: string;
  teacherId: string | null;
  durationMinutes: number;
  notes: string | null;
  organizationId: string;
  summonedBy: string | null;
  summonedTo: string | null;

  // Path information from hall pass type
  hallPassTypePath: {
    pathId: string;
    mapId: string;
    startLocationId: string;
    endLocationId: string;
    waypoints: string; // JSON string
  } | null;

  // Explicit path overrides from hall pass
  explicitPath: {
    pathId: string;
    mapId: string;
    startLocationId: string;
    endLocationId: string;
    waypoints: string; // JSON string
  } | null;

  // Location details
  startLocation: {
    locationId: string;
    name: string;
    locationType: string;
    posX: number;
    posY: number;
  } | null;

  destinationLocation: {
    locationId: string;
    name: string;
    locationType: string;
    posX: number;
    posY: number;
  } | null;

  explicitStartLocation: {
    locationId: string;
    name: string;
    locationType: string;
    posX: number;
    posY: number;
  } | null;

  explicitDestinationLocation: {
    locationId: string;
    name: string;
    locationType: string;
    posX: number;
    posY: number;
  } | null;
}

export interface HallPassTypeResponse {
  hallPassTypeId: string;
  hallPassTypeName: string;
  hallPassTypeDescription: string;
  hallPassTypeDuration: number;
  isDefault: boolean;
  pathId: string | null;
  deactivatedOn: string | null;

  path: {
    pathId: string;
    mapId: string;
    startLocationId: string;
    endLocationId: string;
    waypoints: string; // JSON string
  } | null;

  startLocationDetails: {
    locationId: string;
    name: string;
    locationType: string;
    posX: number;
    posY: number;
  } | null;

  destinationLocationDetails: {
    locationId: string;
    name: string;
    locationType: string;
    posX: number;
    posY: number;
  } | null;
}

export interface MapLocation {
  locationId: string;
  mapId?: string;
  organizationId?: string;
  name: string;
  locationType: string;
  posX: number;
  posY: number;
  parentBuildingId?: string | null;
}

export interface MapPath {
  pathId: string;
  mapId?: string;
  startLocationId: string;
  endLocationId: string;
  waypoints: string; // JSON string of waypoint coordinates
}

export interface MapDetails {
  mapId: string;
  organizationId: string;
  mapName: string;
  backgroundUrl: string;
  widthPx: number;
  heightPx: number;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  locations: MapLocation[];
  paths?: MapPath[];
}

export interface MapLocationUserResponse {
  mapLocationUserId: number;
  locationUserId: string;
  userId: string;
  locationId: string;
  sortOrder?: number;
}

class HallPassApiService {

  // Get all hall passes
  async getHallPasses(): Promise<HallPassResponse[]> {
    const end = new Date();
    const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    const response = await axios.get('/api/HallPasses', {
      params: {
      start: start.toISOString(),
      end: end.toISOString()
      }
    });
    return response.data;
  }

  // Get hall pass types
  async getHallPassTypes(all: boolean = false): Promise<HallPassTypeResponse[]> {
    const response = await axios.get(`/api/HallPassTypes${all ? '?all=true' : ''}`);
    return response.data;
  }

  // Create a hall pass type
  async createHallPassType(data: Partial<HallPassTypeResponse>): Promise<HallPassTypeResponse> {
    const response = await axios.post('/api/HallPassTypes', data);
    return response.data;
  }

  // Update a hall pass type
  async updateHallPassType(id: string, data: Partial<HallPassTypeResponse>): Promise<void> {
    await axios.put(`/api/HallPassTypes/${id}`, data);
  }

  // Delete a hall pass type
  async deleteHallPassType(id: string): Promise<void> {
    await axios.delete(`/api/HallPassTypes/${id}`);
  }

  // Create a new hall pass
  async createHallPass(data: {
    studentId: string;
    teacherId?: string;
    hallPassTypeId?: string;
    organizationId: string;
    summonedBy?: string;
    summonedTo?: string;
    notes?: string;
    durationMinutes?: number;
    pathId?: string;
  }): Promise<HallPassResponse> {
    const response = await axios.post('/api/HallPasses', data);
    return response.data;
  }

  // Return a hall pass
  async returnHallPass(hallPassId: string): Promise<void> {
    await axios.put(`/api/HallPasses/ReturnHallPass/${hallPassId}`);
  }

  // Cancel a hall pass
  async cancelHallPass(hallPassId: string): Promise<void> {
    await axios.put(`/api/HallPasses/CancelHallPass/${hallPassId}`);
  }

  // Update a hall pass
  async updateHallPass(hallPassId: string, data: {
    hallPassStatusId?: number;
    durationMinutes?: number;
    notes?: string;
    pathId?: string;
  }): Promise<HallPassResponse> {
    const response = await axios.put(`/api/HallPasses/${hallPassId}`, data);
    return response.data;
  }
  // Get maps for an organization (or all maps if no org specified)
  async getMaps(_organizationId?: string): Promise<MapDetails[]> {
    const response = await axios.get('/api/Maps');
    let maps: MapDetails[] = response.data;

    if (_organizationId) {
      maps = maps.filter(map => map.organizationId === _organizationId);
    }

    return maps;
  }

  // Get a single map with locations and paths
  async getMap(mapId: string): Promise<MapDetails> {
    const response = await axios.get(`/api/Maps/${mapId}`);
    return response.data as MapDetails;
  }
  // Get or create default map for organization
  async getOrCreateDefaultMap(organizationId: string): Promise<{ mapId: string }> {
    try {
      const maps = await this.getMaps(organizationId);

      if (maps.length > 0) {
        // Return first map for this organization
        return { mapId: maps[0].mapId };
      }

      // If no maps exist, create a default one
      const newMap = {
        organizationId: organizationId,
        mapName: 'Default Map',
        backgroundUrl: 'https://i.imgur.com/4zu4j6p.png',
        widthPx: 1200,
        heightPx: 800,
        isActive: true,
        createdAt: new Date().toISOString()
      };

      const response = await axios.post('/api/Maps', newMap);
      return { mapId: response.data.mapId };

    } catch (error) {
      console.error('Failed to get/create default map:', error);
      throw error;
    }
  }

  // Get map locations
  async getMapLocations(mapId?: string): Promise<MapLocation[]> {
    const endpoint = mapId ? `/api/MapLocations/ByMap/${mapId}` : '/api/MapLocations';
    const response = await axios.get(endpoint);
    return response.data;
  }

  // Get map paths
  async getMapPaths(mapId?: string): Promise<MapPath[]> {
    const endpoint = mapId ? `/api/MapPaths/ByMap/${mapId}` : '/api/MapPaths';
    const response = await axios.get(endpoint);
    return response.data;
  }

  // Create a new map location
  async createMapLocation(data: {
    mapId: string;
    organizationId: string;
    name: string;
    locationType: string;
    posX: number;
    posY: number;
    parentBuildingId?: string;
  }): Promise<MapLocation> {
    const response = await axios.post('/api/MapLocations', data);
    return response.data;
  }  // Update a map location
  async updateMapLocation(locationId: string, data: MapLocation): Promise<void> {
    try {
      console.log('API Service - Updating location:', locationId, 'with data:', data);
      // send same shape as createMapLocation (camelCased), no wrapper
      const payload = {
        LocationId: locationId,
        mapId: data.mapId,
        organizationId: data.organizationId,
        name: data.name,
        locationType: data.locationType,
        posX: Math.round(data.posX),
        posY: Math.round(data.posY),
        parentBuildingId: data.parentBuildingId ?? null
      };
      console.log('API Service - Update payload:', payload);
      await axios.put(`/api/MapLocations/${locationId}`, payload);
      console.log('API Service - Update succeeded');
    } catch (error: any) {
      console.error('API Service - Update failed:', error.response?.data || error.message);
      throw error;
    }
  }

  // Delete a map location
  async deleteMapLocation(locationId: string): Promise<void> {
    await axios.delete(`/api/MapLocations/${locationId}`);
  }

  // Create a new map path
  async createMapPath(data: {
    mapId: string;
    startLocationId: string;
    endLocationId: string;
    waypoints: { x: number; y: number }[];
  }): Promise<MapPath> {
    const pathData = {
      ...data,
      waypoints: JSON.stringify(data.waypoints)
    };
    const response = await axios.post('/api/MapPaths', pathData);
    return response.data;
  }

  // Update a map path
  async updateMapPath(pathId: string, data: {
    startLocationId?: string;
    endLocationId?: string;
    waypoints?: { x: number; y: number }[];
  }): Promise<void> {
    const pathData = {
      ...data,
      waypoints: data.waypoints ? JSON.stringify(data.waypoints) : undefined
    };
    await axios.put(`/api/MapPaths/${pathId}`, pathData);
  }

  // Delete a map path
  async deleteMapPath(pathId: string): Promise<void> {
    await axios.delete(`/api/MapPaths/${pathId}`);
  }

  // Get all user‐location associations
  async getLocationUsers(): Promise<MapLocationUserResponse[]> {
    const response = await axios.get('/api/MapLocationUsers');
    return response.data;
  }

  // Create a new user‐location association
  async createLocationUser(data: { userId: string; locationId: string; sortOrder?: number }): Promise<MapLocationUserResponse> {
    const response = await axios.post('/api/MapLocationUsers', data);
    return response.data;
  }

  // Update an existing user‐location association
  async updateLocationUser(id: string, data: { userId: string; locationId: string; sortOrder?: number }): Promise<void> {
    await axios.put(`/api/MapLocationUsers/${id}`, data);
  }

  // Delete a user‐location association
  async deleteLocationUser(id: string): Promise<void> {
    await axios.delete(`/api/MapLocationUsers/${id}`);
  }

  // Helper function to parse waypoints from JSON string
  parseWaypoints(waypointsJson: string): { x: number; y: number }[] {
    try {
      return JSON.parse(waypointsJson) || [];
    } catch (error) {
      console.error('Failed to parse waypoints:', error);
      return [];
    }
  }

  // Helper function to convert API location to local Location type
  convertApiLocationToLocal(apiLocation: MapLocation): import('../types').Location {
    return {
      id: apiLocation.locationId,
      name: apiLocation.name,
      type: apiLocation.locationType as 'classroom' | 'restroom' | 'building' | 'other',
      position: { x: apiLocation.posX, y: apiLocation.posY },
      ...(apiLocation.locationType === 'classroom' && {
        buildingId: apiLocation.parentBuildingId ?? apiLocation.locationId
      })
    };
  }

  // Helper function to convert API path to local Path type
  convertApiPathToLocal(apiPath: MapPath): import('../types').Path {
    return {
      id: apiPath.pathId,
      startLocationId: apiPath.startLocationId,
      endLocationId: apiPath.endLocationId,
      waypoints: this.parseWaypoints(apiPath.waypoints)
    };
  }

  // Helper function to convert API hall pass to local Student type
  convertApiHallPassToStudent(apiHallPass: HallPassResponse): import('../types').Student {
    // Use explicit path if available, otherwise fall back to hall pass type path
    const pathData = apiHallPass.explicitPath || apiHallPass.hallPassTypePath;
    const startLocation = apiHallPass.explicitStartLocation || apiHallPass.startLocation;
    const destinationLocation = apiHallPass.explicitDestinationLocation || apiHallPass.destinationLocation;

    return {
      userId: apiHallPass.hallPassId,
      name: `Student ${apiHallPass.studentId}`, // You might want to get student name from another endpoint
      startTime: new Date(apiHallPass.requestDecisionedAt || apiHallPass.requestAt).getTime(),
      expectedReturnTime: new Date(apiHallPass.requestDecisionedAt || apiHallPass.requestAt).getTime() + (apiHallPass.durationMinutes * 60 * 1000),
      startLocationId: startLocation?.locationId || '',
      destinationId: destinationLocation?.locationId || '',
      pathId: pathData?.pathId || '',
      returned: !!apiHallPass.returnAt,
    };
  }

  /* ------------------------------------------------------------------ */
  /*  MapPathUser (user path preference ordering)                       */
  /* ------------------------------------------------------------------ */
  /** DTO returned by /api/MapPathUsers */
  async getMapPathUsers(): Promise<MapPathUserAssignment[]> {
    const { data } = await axios.get('/api/MapPathUsers');
    return data.map(this.normalizeMapPathUser);
  }

  /** Filter client‑side by user */
  async getUserPathAssignments(userId: string): Promise<MapPathUserAssignment[]> {
    const all = await this.getMapPathUsers();
    return all.filter(a => a.userId === userId);
  }

  /** Get assignments for a single path */
  async getMapPathUsersByPath(pathId: string): Promise<MapPathUserAssignment[]> {
    const { data } = await axios.get(`/api/MapPathUsers/ByPath/${pathId}`);
    return data.map(this.normalizeMapPathUser);
  }

  /** Create a new assignment (MapPathUser) */
  async createMapPathUser(data: {
    pathId: string;              // Guid
    userId: string;              // Guid
    sortOrder: number;           // 1-based ordering
    isPrimary?: boolean;
  }): Promise<MapPathUserAssignment> {
    const payload = {
      mapPathUserId: 0,
      pathId: data.pathId,
      userId: data.userId,
      sortOrder: data.sortOrder,
      isPrimary: data.isPrimary ?? null
    } as any;
    const { data: created } = await axios.post('/api/MapPathUsers', payload);
    return this.normalizeMapPathUser(created);
  }

  /** Update existing assignment */
  async updateMapPathUser(id: number, data: Partial<Pick<MapPathUserAssignment, 'pathId' | 'userId' | 'sortOrder' | 'isPrimary'>>): Promise<void> {
    // Server expects full entity shape; we send the minimal we have + id mapping
    const payload: any = {
      mapPathUserId: id,
      pathId: data.pathId,
      userId: data.userId,
      sortOrder: data.sortOrder,
      isPrimary: data.isPrimary
    };
    await axios.put(`/api/MapPathUsers/${id}`, payload);
  }

  async deleteMapPathUser(id: number): Promise<void> {
    await axios.delete(`/api/MapPathUsers/${id}`);
  }

  /** Normalize API (Pascal / camel mix) into consistent camelCase */
  private normalizeMapPathUser(api: any): MapPathUserAssignment {
    return {
      id: api.mapPathUserId ?? api.MapPathUserId,
      mapPathUserId: api.mapPathUserId ?? api.MapPathUserId,
      pathId: api.pathId ?? api.PathId,
      userId: api.userId ?? api.UserId,
      sortOrder: api.sortOrder ?? api.SortOrder ?? 0,
      isPrimary: api.isPrimary ?? api.IsPrimary ?? null,
      createdAt: api.createdAt ?? api.CreatedAt ?? null,
      updatedAt: api.updatedAt ?? api.UpdatedAt ?? null,
    };
  }
}

export const hallPassApiService = new HallPassApiService();

// Export type after class to avoid hoist issues
export interface MapPathUserAssignment {
  id: number;              // duplicate of mapPathUserId for generic components
  mapPathUserId: number;   // primary key
  pathId: string;          // Guid
  userId: string;          // Guid
  sortOrder: number;
  isPrimary: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}
